import { useRef, useState } from 'react';

interface InlineEditableNameProps {
  value: string;
  onSave: (next: string) => void;
  /** Classes for the display wrapper + text. */
  className?: string;
  /** Classes for the <input/> shown while editing. */
  inputClassName?: string;
}

/**
 * Inline-renameable text. Shows a pencil affordance (revealed on ancestor `group` hover),
 * swaps to an input on click, commits on blur/Enter, and cancels on Escape.
 * Stops click propagation so it works inside clickable cards/rows.
 */
export default function InlineEditableName({
  value,
  onSave,
  className = '',
  inputClassName = '',
}: InlineEditableNameProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  // Set when the user cancels (Escape) so the resulting blur discards instead of saving.
  const cancelRef = useRef(false);

  function commit() {
    if (cancelRef.current) {
      cancelRef.current = false;
      setEditing(false);

      return;
    }

    const next = draft.trim();

    if (next && next !== value) {
      onSave(next);
    }

    setEditing(false);
  }

  if (editing) {
    return (
      <input
        type="text"
        value={draft}
        autoFocus
        aria-label="Project name"
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.currentTarget.blur(); // single commit path via onBlur (saves)
          } else if (e.key === 'Escape') {
            cancelRef.current = true;
            e.currentTarget.blur(); // single commit path via onBlur (cancels)
          }
        }}
        className={inputClassName}
      />
    );
  }

  return (
    <span className={`inline-flex items-center gap-1.5 min-w-0 ${className}`}>
      <span className="truncate">{value}</span>
      <button
        type="button"
        aria-label="Rename project"
        title="Rename project"
        onClick={(e) => {
          e.stopPropagation();
          setDraft(value);
          cancelRef.current = false;
          setEditing(true);
        }}
        className="ui-btn-icon shrink-0 p-0.5 opacity-0 group-hover:opacity-60 hover:!opacity-100 focus:opacity-100 touch:opacity-60 transition-opacity"
      >
        <span className="i-ph:pencil-simple text-[0.85em]" />
      </button>
    </span>
  );
}
